/**
 * Search overlay
 *
 * Recherche instantanee, matching cote client sur un catalogue de 22 produits
 * charge une seule fois au premier ouverture.
 *
 * Pourquoi ne pas utiliser /search/suggest.json de Shopify : son moteur fait
 * du prefixe. "hydratation" ne matche pas le tag "deshydratation" et
 * "anti-ride" ne matche pas "ride", donc deux des trois termes les plus tapes
 * renvoyaient zero resultat. Le pont de synonymes (rendu dans
 * snippets/search-overlay.liquid) mappe le vocabulaire client vers les tags
 * reels du catalogue.
 */
(() => {
  const overlay = document.querySelector('[data-search-overlay]');
  const configEl = document.querySelector('[data-search-config]');
  if (!overlay || !configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }

  const panel = overlay.querySelector('[data-search-panel]');
  const input = overlay.querySelector('[data-search-input]');
  const body = overlay.querySelector('[data-search-body]');
  const clearBtn = overlay.querySelector('[data-search-clear]');
  const chipsHtml = body ? body.innerHTML : '';

  const MAX_PRODUCTS = 6;
  const MAX_COLLECTIONS = 3;
  const DEBOUNCE_MS = 120;

  let catalog = null;
  let catalogPromise = null;
  let lastFocused = null;

  /* ---------------------------------------------------------------------
     Normalisation : minuscules, sans accent, sans ponctuation.
     "Anti-Âge", "anti age" et "ANTIAGE" convergent vers "anti age".
     --------------------------------------------------------------------- */
  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /* ---------------------------------------------------------------------
     Le filtre `t` de Liquid echappe le HTML avant `| json` : "qu'un" arrive
     en "qu&#39;un". Sans ce decodage, escapeHtml() reechappe le & a
     l'injection et l'entite s'affiche telle quelle a l'ecran. On decode donc
     une fois ici, et l'echappement a l'injection reste en place.
     --------------------------------------------------------------------- */
  const decodeEntities = (value) => {
    const s = String(value == null ? '' : value);
    if (s.indexOf('&') === -1) return s;
    const holder = document.createElement('textarea');
    holder.innerHTML = s;
    return holder.value;
  };

  Object.keys(config.strings || {}).forEach((k) => {
    config.strings[k] = decodeEntities(config.strings[k]);
  });

  const concerns = (config.concerns || []).map((c) => ({
    ...c,
    collection: decodeEntities(c.collection),
    keys: (c.keys || []).map(normalize),
    normTags: (c.tags || []).map(normalize),
  }));

  /* ---------------------------------------------------------------------
     Chargement du catalogue
     --------------------------------------------------------------------- */
  const loadCatalog = () => {
    if (catalog) return Promise.resolve(catalog);
    if (catalogPromise) return catalogPromise;

    // Pas d'en-tete Accept: application/json ici. Shopify prend alors la
    // requete pour un appel d'API sur /collections/all et repond 404, alors
    // que la meme URL sans cet en-tete rend bien le template. Response.json()
    // parse le corps quel que soit le content-type, donc rien a compenser.
    catalogPromise = fetch(config.catalogUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        catalog = (data && data.products ? data.products : []).map((p) => ({
          ...p,
          normTitle: normalize(p.title),
          normTags: (p.tags || []).map(normalize),
        }));
        return catalog;
      })
      .catch(() => {
        catalog = [];
        return catalog;
      });

    return catalogPromise;
  };

  /* ---------------------------------------------------------------------
     Matching
     --------------------------------------------------------------------- */
  const matchConcerns = (q) => {
    if (q.length < 2) return [];
    return concerns.filter((c) =>
      c.keys.some((k) => k === q || k.startsWith(q) || (k.length >= 3 && q.startsWith(k)))
    );
  };

  const matchProducts = (q, matched) => {
    if (!catalog) return [];

    const wantedTags = new Set();
    const promoted = new Set();
    matched.forEach((c) => {
      c.normTags.forEach((t) => wantedTags.add(t));
      (c.products || []).forEach((h) => promoted.add(h));
    });

    // Score additif : un produit qui matche a la fois par le titre et par le
    // tag doit passer devant un produit qui ne matche que par le tag. Sans ca,
    // "creme de nuit" sortait le SPF50 en premier, les deux portant le tag
    // "creme", l'ordre du catalogue tranchant a leur place.
    const scored = [];
    catalog.forEach((p) => {
      let score = 0;
      if (promoted.has(p.handle)) score += 4;
      if (p.normTags.some((t) => wantedTags.has(t))) score += 2;
      if (q.length >= 2 && p.normTitle.includes(q)) {
        score += p.normTitle === q || p.normTitle.startsWith(q) ? 3 : 2;
      }
      if (score > 0) scored.push({ p, score });
    });

    // score decroissant, puis ordre du catalogue pour rester stable
    return scored.sort((a, b) => b.score - a.score).map((s) => s.p);
  };

  /* ---------------------------------------------------------------------
     Rendu
     --------------------------------------------------------------------- */
  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  const arrow =
    '<svg class="search-overlay__result-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none"' +
    ' stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' +
    ' aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6"/></svg>';

  const renderIdle = () => {
    body.innerHTML = chipsHtml;
  };

  const renderResults = (q, matched, products) => {
    if (!matched.length && !products.length) {
      const title = String(config.strings.emptyTitle || '').split('%%term%%').join(escapeHtml(input.value.trim()));
      body.innerHTML =
        '<p class="search-overlay__empty-title">' + title + '</p>' +
        '<p class="search-overlay__empty-body p-small">' + escapeHtml(config.strings.emptyBody) + '</p>' +
        chipsHtml;
      return;
    }

    let html = '';
    let i = 0;

    const cols = matched.filter((c) => c.collection && c.url).slice(0, MAX_COLLECTIONS);
    if (cols.length) {
      html += '<p class="search-overlay__label p-extra-small">' + escapeHtml(config.strings.collections) + '</p>';
      html += '<ul class="search-overlay__list">';
      cols.forEach((c) => {
        html +=
          '<li class="search-overlay__item"><a class="search-overlay__result" href="' + escapeHtml(c.url) +
          '" style="--i:' + i++ + '">' +
          '<span class="search-overlay__result-text">' +
          '<span class="search-overlay__result-title p-small">' + escapeHtml(c.collection) + '</span>' +
          '<span class="search-overlay__result-meta p-extra-small">' + escapeHtml(config.strings.viewCollection) + '</span>' +
          '</span>' + arrow + '</a></li>';
      });
      html += '</ul>';
    }

    const prods = products.slice(0, MAX_PRODUCTS);
    if (prods.length) {
      html += '<p class="search-overlay__label p-extra-small">' + escapeHtml(config.strings.products) + '</p>';
      html += '<ul class="search-overlay__list">';
      prods.forEach((p) => {
        const meta = p.available === false ? escapeHtml(config.strings.soldOut) : escapeHtml(p.price || '');
        const img = p.image
          ? '<img class="search-overlay__thumb" src="' + escapeHtml(p.image) +
            // pas de loading="lazy" : dans un panneau en position fixed qui vient
            // d'apparaitre, l'observateur d'intersection ne se declenche pas et les
            // vignettes restent vides. Six images de 120px, le cout est nul.
            '" alt="" width="48" height="48" loading="eager" decoding="async">'
          : '<span class="search-overlay__thumb"></span>';
        html +=
          '<li class="search-overlay__item"><a class="search-overlay__result" href="' + escapeHtml(p.url) +
          '" style="--i:' + i++ + '">' + img +
          '<span class="search-overlay__result-text">' +
          '<span class="search-overlay__result-title p-small">' + escapeHtml(p.title) + '</span>' +
          '<span class="search-overlay__result-meta p-extra-small">' + meta + '</span>' +
          '</span>' + arrow + '</a></li>';
      });
      html += '</ul>';
    }

    body.innerHTML = html;
  };

  const run = () => {
    const raw = input.value.trim();
    clearBtn.hidden = raw.length === 0;

    if (!raw) {
      renderIdle();
      return;
    }

    const q = normalize(raw);
    loadCatalog().then(() => {
      const matched = matchConcerns(q);
      renderResults(q, matched, matchProducts(q, matched));
    });
  };

  let timer = null;
  const scheduleRun = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(run, DEBOUNCE_MS);
  };

  /* ---------------------------------------------------------------------
     Ouverture / fermeture
     --------------------------------------------------------------------- */
  const open = (trigger) => {
    if (!overlay.hidden) return;
    // On memorise le declencheur plutot que document.activeElement : un clic
    // souris ne donne pas toujours le focus au bouton, et le focus n'avait
    // alors nulle part ou revenir a la fermeture.
    lastFocused = trigger || document.activeElement;
    loadCatalog();

    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    renderIdle();

    // Le passage de display:none a visible et l'ajout de la classe dans la
    // meme frame font sauter la transition : le navigateur n'a jamais calcule
    // l'etat ferme. Cette lecture force ce calcul avant la bascule.
    void overlay.offsetWidth;

    overlay.classList.add('search-overlay--open');
    window.requestAnimationFrame(() => input.focus());
  };

  const close = () => {
    if (overlay.hidden) return;
    overlay.classList.remove('search-overlay--open');
    document.body.style.overflow = '';

    window.setTimeout(() => {
      overlay.hidden = true;
      input.value = '';
      clearBtn.hidden = true;
      renderIdle();
    }, 260);

    if (lastFocused && lastFocused.focus) lastFocused.focus();
  };

  /* ---------------------------------------------------------------------
     Evenements
     --------------------------------------------------------------------- */
  document.querySelectorAll('[data-search-toggle]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      open(btn);
    });
  });

  overlay.querySelectorAll('[data-search-dismiss]').forEach((el) => {
    el.addEventListener('click', close);
  });

  clearBtn.addEventListener('click', () => {
    input.value = '';
    clearBtn.hidden = true;
    renderIdle();
    input.focus();
  });

  input.addEventListener('input', scheduleRun);

  // Les puces de suggestion sont recreees a chaque rendu, donc delegation.
  body.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-search-term]');
    if (!chip) return;
    input.value = chip.textContent.trim();
    clearBtn.hidden = false;
    run();
    input.focus();
  });

  const focusables = () =>
    Array.from(panel.querySelectorAll('a[href], button:not([hidden]), input')).filter(
      (el) => el.offsetParent !== null
    );

  document.addEventListener('keydown', (e) => {
    if (overlay.hidden) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = Array.from(panel.querySelectorAll('.search-overlay__result'));
      if (!items.length) return;
      e.preventDefault();
      const current = items.indexOf(document.activeElement);
      const next =
        e.key === 'ArrowDown'
          ? (current + 1 + items.length) % items.length
          : (current - 1 + items.length) % items.length;
      items[current === -1 && e.key === 'ArrowUp' ? items.length - 1 : next].focus();
      return;
    }

    if (e.key === 'Enter' && document.activeElement === input) {
      const first = panel.querySelector('.search-overlay__result');
      if (first) {
        e.preventDefault();
        first.click();
      }
      return;
    }

    // piege a focus : on ne sort pas du panneau au clavier
    if (e.key === 'Tab') {
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
})();
